<template>
  <section class="bg-[radial-gradient(circle_at_top_left,#fff9f1_0%,#f8f1ea_38%,#f3ebe3_100%)] py-5 sm:py-6 lg:py-8">
    <div class="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8">
      <div v-if="loading" class="rounded-[28px] border border-dashed border-[#eadfd4] bg-white/90 p-8 text-sm text-muted shadow-sm">
        Loading venue details...
      </div>

      <div v-else class="space-y-6 lg:space-y-8">
        <div v-if="error" class="rounded-[22px] border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-medium text-amber-800">
          {{ error }}
        </div>

        <section class="overflow-hidden rounded-[34px] border border-[#eadfd4] bg-[linear-gradient(180deg,#fffaf5_0%,#fffdfb_100%)] shadow-[0_18px_55px_rgba(79,55,36,0.08)]">
          <div class="grid xl:grid-cols-[minmax(0,1.08fr)_420px]">
            <div class="p-5 sm:p-7 lg:p-8">
              <div class="flex flex-wrap items-start justify-between gap-4">
                <div class="max-w-3xl">
                  <p class="text-xs font-semibold uppercase tracking-[0.28em] text-[#b78969]">Public Venue Detail</p>
                  <h1 class="mt-3 font-serif text-[2.1rem] font-semibold leading-[1.05] text-ink sm:text-[2.6rem] lg:text-[3.15rem]">
                    {{ venue.title }}
                  </h1>
                  <p class="mt-3 max-w-2xl font-serif text-lg italic text-[#927660] sm:text-[1.3rem]">
                    {{ venue.tagLine || 'Venue details available for your event invite.' }}
                  </p>
                </div>

                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="item in heroBadges"
                    :key="item.label"
                    class="rounded-full px-3 py-1.5 text-xs font-semibold shadow-sm"
                    :class="item.tone"
                  >
                    {{ item.label }}
                  </span>
                </div>
              </div>

              <div class="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <article v-for="fact in heroFacts" :key="fact.label" class="rounded-[22px] border border-[#eadfd4] bg-white/90 px-4 py-4 shadow-sm">
                  <p class="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#b78969]">{{ fact.label }}</p>
                  <p class="mt-2 text-sm font-semibold text-ink sm:text-[0.98rem]">{{ fact.value }}</p>
                </article>
              </div>

              <div class="mt-7 grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
                <div>
                  <p class="text-[1.02rem] leading-8 text-[#2f241b] sm:text-[1.08rem]">
                    {{ venue.about || 'Venue description is not available yet.' }}
                  </p>

                  <div class="mt-5 rounded-[20px] border border-[#ead8b8] bg-[#fff8e6] px-4 py-4 text-[0.98rem] italic leading-7 text-[#6f532c] sm:px-5">
                    <span class="font-semibold">💡 {{ inviteCallout }}</span>
                  </div>
                </div>

                <div class="rounded-[24px] border border-[#eadfd4] bg-[#f8f2eb] p-5">
                  <p class="text-xs font-semibold uppercase tracking-[0.2em] text-[#b78969]">Quick profile</p>
                  <div class="mt-4 space-y-3 text-sm text-[#4b3b2f]">
                    <div class="flex items-center justify-between gap-3">
                      <span>Venue type</span>
                      <span class="text-right font-semibold text-ink">{{ venue.typeLabel }}</span>
                    </div>
                    <div class="flex items-center justify-between gap-3">
                      <span>Established</span>
                      <span class="text-right font-semibold text-ink">{{ establishedLabel }}</span>
                    </div>
                    <div class="flex items-center justify-between gap-3">
                      <span>Phone</span>
                      <span class="text-right font-semibold text-ink">{{ venue.phone || 'Not shared' }}</span>
                    </div>
                    <div class="flex items-center justify-between gap-3">
                      <span>Deposit</span>
                      <span class="text-right font-semibold text-ink">{{ depositLabel }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-7 grid gap-5 lg:grid-cols-2">
                <div v-if="venue.highlights.length">
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Highlights</p>
                  <div class="mt-3 flex flex-wrap gap-2">
                    <span v-for="item in venue.highlights" :key="item" class="rounded-full bg-[#f5eee8] px-3 py-1.5 text-sm font-semibold text-[#6d5542]">
                      {{ item }}
                    </span>
                  </div>
                </div>

                <div v-if="venue.inHouseServices.length">
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">In-house services</p>
                  <div class="mt-3 grid gap-3 sm:grid-cols-2">
                    <div v-for="item in venue.inHouseServices" :key="item" class="flex items-center gap-3 text-sm text-[#2f241b] sm:text-[0.98rem]">
                      <span class="text-[#2e8a5d]">✓</span>
                      <span>{{ item }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="border-t border-[#eadfd4] bg-[#f4e9dd] p-4 sm:p-5 xl:border-l xl:border-t-0">
              <div class="mb-4 flex items-center justify-between gap-3">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#9f7a5d]">Gallery preview</p>
                  <p class="mt-1 text-sm text-[#6f5948]">Tap any image to open the full gallery</p>
                </div>
                <button type="button" class="rounded-full border border-[#d8c7b8] bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#6f5948] transition hover:border-brand hover:text-brand" @click="openInviteGallery()">
                  View all
                </button>
              </div>

              <div class="grid grid-cols-2 gap-3 auto-rows-[108px] sm:auto-rows-[132px]">
                <button
                  type="button"
                  class="group relative col-span-2 row-span-2 overflow-hidden rounded-[24px] bg-[linear-gradient(135deg,#f5e8da_0%,#f9f1e8_54%,#efe1d1_100%)] text-left"
                  @click="openInviteGalleryById(heroGallery[0])"
                >
                  <img v-if="heroGallery[0]" :src="heroGallery[0].src" :alt="venue.title" class="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]">
                  <div class="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1f1208]/70 to-transparent px-4 py-4 text-white">
                    <div class="flex flex-wrap gap-2">
                      <span v-if="heroGallery[0]?.isCover" class="rounded-full bg-white/95 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand">
                        Cover image
                      </span>
                      <span v-if="heroGallery[0]?.isPlaceholder" class="rounded-full bg-[#1f1208]/55 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
                        Preview placeholder
                      </span>
                    </div>
                  </div>
                </button>

                <button
                  v-for="(image, index) in heroGallery.slice(1, 5)"
                  :key="image.id || index"
                  type="button"
                  class="group relative overflow-hidden rounded-[18px] bg-[linear-gradient(135deg,#f5e8da_0%,#f9f1e8_54%,#efe1d1_100%)] text-left"
                  @click="openInviteGalleryById(image)"
                >
                  <img :src="image.src" :alt="`${venue.title} ${index + 2}`" class="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]">
                  <div
                    v-if="image.overlayText"
                    class="absolute inset-0 flex flex-col items-center justify-center bg-[#1f1208]/55 text-center text-white"
                  >
                    <span class="text-[1.45rem] font-semibold leading-none">{{ image.overlayText }}</span>
                    <span class="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.18em]">more photos</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </section>

        <div class="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div class="space-y-6">
            <section class="rounded-[28px] border border-[#eadfd4] bg-white/95 p-5 shadow-sm sm:p-6">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Spaces</p>
                <h2 class="mt-2 font-serif text-[1.7rem] font-semibold text-ink sm:text-[1.9rem]">Venue spaces</h2>
              </div>

              <div v-if="spaces.length" class="mt-5 grid gap-4 md:grid-cols-2">
                <article v-for="item in spaces" :key="item.id" class="rounded-[24px] border border-[#eadfd4] bg-[#fffaf5] p-5">
                  <div class="flex items-start justify-between gap-4">
                    <div>
                      <h3 class="text-lg font-semibold text-ink">{{ item.name }}</h3>
                      <p class="mt-1 text-sm text-muted">{{ item.typeLabel }} · {{ item.setting }}</p>
                    </div>
                    <p class="text-base font-semibold text-[#1f1208]">{{ item.priceLabel }}</p>
                  </div>
                  <p class="mt-4 text-sm leading-7 text-[#4b3b2f]">{{ item.description || 'No description added.' }}</p>
                  <div v-if="item.seatingSummary.length" class="mt-4 flex flex-wrap gap-2">
                    <span v-for="seat in item.seatingSummary" :key="seat" class="rounded-full border border-[#eadfd4] bg-white px-3 py-1 text-xs font-semibold text-[#6f5948]">
                      {{ seat }}
                    </span>
                  </div>
                </article>
              </div>
              <p v-else class="mt-5 rounded-[22px] border border-dashed border-[#eadfd4] bg-[#fffaf5] px-5 py-5 text-sm text-muted">
                No spaces configured yet.
              </p>
            </section>

            <section class="rounded-[28px] border border-[#eadfd4] bg-white/95 p-5 shadow-sm sm:p-6">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Packages</p>
                <h2 class="mt-2 font-serif text-[1.7rem] font-semibold text-ink sm:text-[1.9rem]">Venue packages</h2>
              </div>

              <div v-if="packages.length" class="mt-5 grid gap-4 md:grid-cols-2">
                <article v-for="item in packages" :key="item.id" class="rounded-[24px] border border-[#eadfd4] bg-[#fffaf5] p-5">
                  <div class="flex items-start justify-between gap-4">
                    <div>
                      <h3 class="text-lg font-semibold text-ink">{{ item.name }}</h3>
                      <div class="mt-2 flex flex-wrap gap-2 text-xs text-[#7d634e]">
                        <span class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.priceLabel }}</span>
                        <span v-if="item.maxGuests" class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.maxGuests }} guests</span>
                        <span v-if="item.validHours" class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.validHours }} hrs</span>
                      </div>
                    </div>
                  </div>
                  <p class="mt-4 text-sm leading-7 text-[#4b3b2f]">{{ item.description || 'No description added.' }}</p>
                  <div v-if="item.includedServices.length" class="mt-4 flex flex-wrap gap-2">
                    <span v-for="service in item.includedServices" :key="service" class="rounded-full border border-[#eadfd4] bg-white px-3 py-1 text-xs font-semibold text-[#6f5948]">
                      {{ service }}
                    </span>
                  </div>
                </article>
              </div>
              <p v-else class="mt-5 rounded-[22px] border border-dashed border-[#eadfd4] bg-[#fffaf5] px-5 py-5 text-sm text-muted">
                No packages configured yet.
              </p>
            </section>

            <section class="rounded-[28px] border border-[#eadfd4] bg-white/95 p-5 shadow-sm sm:p-6">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Policies</p>
                <h2 class="mt-2 font-serif text-[1.7rem] font-semibold text-ink sm:text-[1.9rem]">Venue policies</h2>
              </div>

              <div class="mt-6 grid gap-8 md:grid-cols-2">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue rules</p>
                  <div class="mt-5 space-y-5">
                    <article v-for="item in venueRuleItems" :key="item.label" class="flex items-start gap-4">
                      <span class="mt-0.5 text-[1.35rem]">{{ item.icon }}</span>
                      <div>
                        <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">{{ item.label }}</p>
                        <p class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ item.value }}</p>
                      </div>
                    </article>
                  </div>
                </div>

                <div>
                  <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Booking terms</p>
                  <div class="mt-5 space-y-5">
                    <article v-for="item in bookingTermItems" :key="item.label" class="flex items-start gap-4">
                      <span class="mt-0.5 text-[1.35rem]">{{ item.icon }}</span>
                      <div>
                        <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">{{ item.label }}</p>
                        <p class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ item.value }}</p>
                      </div>
                    </article>
                  </div>
                </div>
              </div>
            </section>

            <section class="rounded-[28px] border border-[#eadfd4] bg-white/95 p-5 shadow-sm sm:p-6">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Gallery</p>
                <h2 class="mt-2 font-serif text-[1.7rem] font-semibold text-ink sm:text-[1.9rem]">Photo library snapshot</h2>
              </div>

              <div v-if="galleryPreviewCards.length" class="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                <button
                  v-for="item in galleryPreviewCards"
                  :key="item.id"
                  type="button"
                  class="group overflow-hidden rounded-[20px] border border-[#eadfd4] bg-[#fffaf5] text-left transition hover:border-[#d9c2ac]"
                  @click="openInviteGalleryById(item)"
                >
                  <div class="relative h-28 bg-[linear-gradient(135deg,#f5e8da_0%,#f9f1e8_54%,#efe1d1_100%)] sm:h-32">
                    <img :src="item.src" :alt="venue.title" class="h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]">
                    <span v-if="item.isCover" class="absolute left-3 top-3 rounded-full bg-white/92 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-brand shadow-sm">
                      Cover
                    </span>
                  </div>
                  <div class="px-3 py-3">
                    <p class="truncate text-[11px] font-semibold uppercase tracking-wide text-[#b78969]">Venue photo</p>
                  </div>
                </button>
              </div>
              <p v-else class="mt-5 rounded-[22px] border border-dashed border-[#eadfd4] bg-[#fffaf5] px-5 py-5 text-sm text-muted">
                No gallery images uploaded yet.
              </p>
            </section>
          </div>

          <aside class="self-start xl:sticky xl:top-6">
            <section class="overflow-hidden rounded-[28px] border border-[#eadfcf] bg-[#fcf7eb] shadow-[0_14px_40px_rgba(79,55,36,0.08)]">
              <div class="border-b border-[#eadfcf] px-5 py-5">
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#9e7b43]">Venue contact</p>
                <h3 class="mt-3 font-serif text-[1.6rem] font-semibold text-ink">{{ venue.title }}</h3>
                <p class="mt-1 text-sm text-[#7c6550]">
                  {{ venue.typeLabel }} · {{ depositLabel }}
                </p>
              </div>

              <div class="space-y-4 px-5 py-5">
                <div class="rounded-[18px] border border-[#eadfcf] bg-white px-4 py-4">
                  <p class="text-xs font-semibold uppercase tracking-[0.16em] text-[#9e7b43]">Address</p>
                  <p class="mt-2 text-sm leading-6 text-[#4b3b2f]">{{ venue.address }}</p>
                </div>

                <div class="rounded-[18px] border border-[#eadfcf] bg-white px-4 py-4">
                  <p class="text-xs font-semibold uppercase tracking-[0.16em] text-[#9e7b43]">Phone</p>
                  <p class="mt-2 text-sm leading-6 text-[#4b3b2f]">{{ venue.phone || 'Not shared' }}</p>
                </div>

                <div v-if="venue.email" class="rounded-[18px] border border-[#eadfcf] bg-white px-4 py-4">
                  <p class="text-xs font-semibold uppercase tracking-[0.16em] text-[#9e7b43]">Email</p>
                  <p class="mt-2 text-sm leading-6 text-[#4b3b2f] break-all">{{ venue.email }}</p>
                </div>

                <a
                  v-if="venue.website"
                  :href="venue.website"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex w-full items-center justify-center rounded-[16px] bg-[#cda226] px-5 py-4 text-sm font-semibold text-white transition hover:bg-[#b58d1d]"
                >
                  Visit website ↗
                </a>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import lightGallery from 'lightgallery'
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import lgZoom from 'lightgallery/plugins/zoom'
import api from '@/services/api'

const route = useRoute()

const loading = ref(false)
const error = ref('')
const venue = ref({
  id: '',
  title: '',
  tagLine: '',
  about: '',
  address: '',
  phone: '',
  email: '',
  website: '',
  typeLabel: 'Venue',
  yearEstablished: '',
  highlights: [],
  inHouseServices: []
})
const gallery = ref([])
const spaces = ref([])
const packages = ref([])
const inviteGalleryLightbox = ref(null)
const policies = ref({
  inHouseCatering: '',
  outsideCatering: '',
  decorPolicy: '',
  musicPolicy: '',
  alcoholInhouse: '',
  alcoholOutside: '',
  minGuests: '',
  maxGuests: '',
  decorTime: '',
  cleanTime: '',
  noiseMusic: '',
  otherHouseRules: '',
  cancelTerms: '',
  depositPercent: ''
})

const splitToArray = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)

const formatCurrency = (value) => {
  const numeric = Number(String(value || '').replace(/[^\d.]/g, ''))
  return Number.isFinite(numeric) && numeric > 0 ? `Rs. ${numeric.toLocaleString('en-IN')}` : ''
}

const normalizeGallery = (items = []) =>
  items
    .map((item, index) => ({
      id: String(item?.id || `gallery-${index}`),
      src: String(item?.image || '').trim(),
      isCover: String(item?.is_cover || '0') === '1'
    }))
    .filter((item) => item.src)
    .sort((left, right) => Number(right.isCover) - Number(left.isCover))

const placeholderGallery = Array.from({ length: 5 }).map((_, index) => ({
  id: `placeholder-${index}`,
  src: `https://picsum.photos/seed/venue-${index + 1}/900/700`,
  isCover: index === 0,
  isPlaceholder: true
}))

const lightboxGalleryImages = computed(() => {
  const source = gallery.value.length ? gallery.value : placeholderGallery
  const hiddenCount = Math.max(0, source.length - 5)

  return source.map((item, index) => ({
    ...item,
    overlayText: hiddenCount && index === 4 ? `+${hiddenCount}` : ''
  }))
})

const heroGallery = computed(() => lightboxGalleryImages.value.slice(0, 5))
const galleryPreviewCards = computed(() => lightboxGalleryImages.value.slice(0, 8))
const establishedLabel = computed(() => venue.value.yearEstablished ? `Est. ${venue.value.yearEstablished}` : 'Year not specified')
const depositLabel = computed(() => policies.value.depositPercent ? `${policies.value.depositPercent}% deposit` : 'Deposit on request')

const heroBadges = computed(() => {
  const badges = [
    { label: venue.value.typeLabel || 'Venue', tone: 'bg-[#fff2ea] text-brand' },
    { label: 'Invite preview', tone: 'bg-[#eef9f3] text-[#2d8b62]' }
  ]

  venue.value.highlights.slice(0, 2).forEach((item) => {
    badges.push({ label: item, tone: 'bg-[#f3ede7] text-[#6f5948]' })
  })

  return badges
})

const heroFacts = computed(() => ([
  {
    label: 'Location',
    value: venue.value.address || 'Location unavailable'
  },
  {
    label: 'Established',
    value: establishedLabel.value
  },
  {
    label: 'Guests',
    value: policies.value.minGuests || policies.value.maxGuests
      ? `${policies.value.minGuests || '0'} - ${policies.value.maxGuests || '0'} guests`
      : 'Not specified'
  },
  {
    label: 'Phone',
    value: venue.value.phone || 'Not shared'
  }
]))

const inviteCallout = computed(() => {
  const venueTitle = String(venue.value.title || 'This venue')
  const spaceCount = Number(spaces.value.length || 0)

  if (spaceCount) {
    return `${venueTitle} includes ${spaceCount} event space${spaceCount === 1 ? '' : 's'} and is ready for invite-driven venue discovery.`
  }
  return `${venueTitle} is shared here as a public venue preview with event-ready details, policies, and photo access.`
})

const venueRuleItems = computed(() => [
  { icon: '🍽️', label: 'Catering', value: policies.value.inHouseCatering || 'Not specified' },
  { icon: '🌸', label: 'Decoration', value: policies.value.decorPolicy || 'Not specified' },
  { icon: '🎵', label: 'DJ / Music', value: policies.value.musicPolicy || 'Not specified' },
  { icon: '🥂', label: 'Alcohol', value: policies.value.alcoholInhouse === '1' ? 'In-house alcohol available.' : 'Alcohol policy not specified.' },
  { icon: '📋', label: 'House rules', value: policies.value.otherHouseRules || 'Not specified' }
])

const bookingTermItems = computed(() => [
  { icon: '👥', label: 'Guest count', value: policies.value.minGuests || policies.value.maxGuests ? `${policies.value.minGuests || '0'} - ${policies.value.maxGuests || '0'} guests` : 'Not specified' },
  { icon: '🛠️', label: 'Setup time', value: policies.value.decorTime || 'Not specified' },
  { icon: '🧹', label: 'Cleanup time', value: policies.value.cleanTime || 'Not specified' },
  { icon: '💳', label: 'Deposit', value: policies.value.depositPercent ? `${policies.value.depositPercent}% of total` : 'Not specified' },
  { icon: '↩️', label: 'Cancellation', value: policies.value.cancelTerms || 'Not specified' }
])

const destroyInviteGallery = () => {
  if (inviteGalleryLightbox.value) {
    inviteGalleryLightbox.value.destroy(true)
    inviteGalleryLightbox.value = null
  }
}

const openInviteGallery = (startIndex = 0) => {
  if (!lightboxGalleryImages.value.length) return
  destroyInviteGallery()
  inviteGalleryLightbox.value = lightGallery(document.createElement('div'), {
    dynamic: true,
    dynamicEl: lightboxGalleryImages.value.map((image) => ({
      src: image.src,
      thumb: image.src
    })),
    index: Math.max(0, startIndex),
    plugins: [lgThumbnail, lgZoom],
    download: false,
    actualSize: false,
    mobileSettings: {
      controls: true,
      showCloseIcon: true,
      download: false
    }
  })
  inviteGalleryLightbox.value.openGallery(startIndex)
}

const openInviteGalleryById = (image) => {
  const imageId = String(image?.id || '')
  const startIndex = lightboxGalleryImages.value.findIndex((item) => String(item.id || '') === imageId)
  openInviteGallery(startIndex >= 0 ? startIndex : 0)
}

const loadVenueDetail = async () => {
  loading.value = true
  error.value = ''
  try {
    const formData = new FormData()
    formData.append('venue_id', String(route.params.venueId || ''))
    const { data } = await api.getVenueDetailInvite(formData)
    const payload = data?.data || {}
    if (!payload || !Object.keys(payload).length) {
      throw new Error(data?.msg || 'Venue details not available.')
    }

    venue.value = {
      id: String(payload?.id || route.params.venueId || ''),
      title: payload?.title || 'Untitled venue',
      tagLine: payload?.tag_line || '',
      about: payload?.about || '',
      address: payload?.address || 'Location unavailable',
      phone: payload?.phone || '',
      email: payload?.email || '',
      website: payload?.website || '',
      typeLabel: payload?.venue_type || payload?.type || 'Venue',
      yearEstablished: payload?.est_year || '',
      highlights: splitToArray(payload?.highlights),
      inHouseServices: splitToArray(payload?.in_house_services)
    }

    gallery.value = normalizeGallery(payload?.gallery_info || [])
    spaces.value = Array.isArray(payload?.spaces_info)
      ? payload.spaces_info.map((item, index) => ({
          id: String(item?.id || `space-${index}`),
          name: item?.name || 'Untitled space',
          typeLabel: item?.type_name || item?.type || 'Space',
          setting: item?.setting || 'Indoor',
          description: item?.description || '',
          seatingSummary: splitToArray(item?.seat_config).map((label, seatIndex) => {
            const capacities = splitToArray(item?.seat_capacity)
            const capacity = capacities[seatIndex]
            return capacity ? `${label}: ${capacity}` : label
          }),
          priceLabel: [formatCurrency(item?.min_price), formatCurrency(item?.max_price)].filter(Boolean).join(' - ') || 'Price on request'
        }))
      : []

    packages.value = Array.isArray(payload?.venue_packages)
      ? payload.venue_packages.map((item, index) => ({
          id: String(item?.id || `package-${index}`),
          name: item?.name || 'Untitled package',
          description: item?.description || '',
          maxGuests: item?.max_guests || '',
          validHours: item?.valid_hours || '',
          includedServices: splitToArray(item?.included_services),
          priceLabel: formatCurrency(item?.price) || 'Price on request'
        }))
      : []

    policies.value = {
      inHouseCatering: payload?.venue_policies?.in_house_catering || '',
      outsideCatering: payload?.venue_policies?.outside_catering || '',
      decorPolicy: payload?.venue_policies?.decor_policy || '',
      musicPolicy: payload?.venue_policies?.music_policy || '',
      alcoholInhouse: String(payload?.venue_policies?.alcohol_inhouse || ''),
      alcoholOutside: String(payload?.venue_policies?.alcohol_outside || ''),
      minGuests: payload?.venue_policies?.min_guests || '',
      maxGuests: payload?.venue_policies?.max_guests || '',
      decorTime: payload?.venue_policies?.decor_time || '',
      cleanTime: payload?.venue_policies?.clean_time || '',
      noiseMusic: payload?.venue_policies?.noise_music || '',
      otherHouseRules: payload?.venue_policies?.other_house_rules || '',
      cancelTerms: payload?.venue_policies?.cancel_terms || '',
      depositPercent: payload?.venue_policies?.deposit_percent || ''
    }

    if (data?.status !== 'ok' && data?.msg) {
      error.value = data.msg
    }
  } catch (err) {
    error.value = err?.message || 'Failed to load venue details.'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadVenueDetail()
})

onBeforeUnmount(() => {
  destroyInviteGallery()
})
</script>
